import type { Locale } from "@/lib/locale";

/**
 * Delta — proprietary tool for product data and feed management.
 * Pure CSS visual, no external assets. Locale-aware copy.
 */
const copy = {
  de: {
    eyebrow: "Proprietäres Tool",
    headlineLead: "Delta —",
    headlineTail: "Produktdaten als Performance-Hebel.",
    body:
      "Die meisten Performance-Probleme im E-Commerce sind keine Kanal-Probleme, sondern Datenprobleme. Delta ist unser Feed-Management-Layer: Produktdaten aus PIM, ERP und Shop fließen in eine kontrollierte Pipeline, werden normalisiert, optimiert und sortiert für jede Plattform ausgespielt — ohne Excel-Pingpong und ohne Mapping-Schicht auf Mandantenseite.",
    bullets: [
      "Saubere und skalierbare Produktfeeds — ohne SKU-Limit.",
      "Plattformspezifische Optimierung statt One-Size-Feeds.",
      "Versioniert, auditierbar, ohne externe Lizenzkosten.",
    ],
    pipelineEyebrow: "Pipeline · vereinfacht",
    layers: [
      { label: "Quellen", note: "PIM · ERP · Shop · CSV" },
      { label: "Normalisierung", note: "Mapping · Validierung · Anreicherung" },
      { label: "Optimierung", note: "Titel · Attribute · Pricing-Signale" },
      { label: "Plattformen", note: "Google · Meta · TikTok · LinkedIn · Microsoft" },
    ],
    footerLeft: "proprietär",
    footerRight: "in produktivem Einsatz",
  },
  en: {
    eyebrow: "Proprietary Tool",
    headlineLead: "Delta —",
    headlineTail: "Product Data as a Performance Lever.",
    body:
      "Most performance problems in e-commerce aren't channel problems — they're data problems. Delta is our feed-management layer: product data from PIM, ERP, and shop flows into a controlled pipeline, gets normalized, optimized, and prepared per platform — without Excel ping-pong and without a mapping layer on the client side.",
    bullets: [
      "Clean, scalable product feeds — no SKU limit.",
      "Platform-specific optimization instead of one-size feeds.",
      "Versioned, auditable, without external license fees.",
    ],
    pipelineEyebrow: "Pipeline · simplified",
    layers: [
      { label: "Sources", note: "PIM · ERP · Shop · CSV" },
      { label: "Normalization", note: "Mapping · Validation · Enrichment" },
      { label: "Optimization", note: "Titles · Attributes · Pricing Signals" },
      { label: "Platforms", note: "Google · Meta · TikTok · LinkedIn · Microsoft" },
    ],
    footerLeft: "proprietary",
    footerRight: "in productive use",
  },
} as const;

const layerColors = ["bg-white/10", "bg-white/15", "bg-lime/20", "bg-cyan/30"];

export function DeltaHero({ locale = "de" }: { locale?: Locale }) {
  const c = copy[locale];
  return (
    <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr] lg:gap-16">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-lime">
          {c.eyebrow}
        </p>
        <h3 className="mt-4 font-display text-3xl leading-tight text-white md:text-4xl">
          {c.headlineLead}
          <br />
          {c.headlineTail}
        </h3>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
          {c.body}
        </p>
        <ul className="mt-8 space-y-3 text-sm text-white/85">
          {c.bullets.map((b) => (
            <li key={b} className="flex gap-3">
              <span className="mt-2 inline-block h-1 w-1 shrink-0 rounded-full bg-lime" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <div
        aria-hidden
        className="relative rounded-lg border border-white/10 bg-white/5 p-6 md:p-8"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/75">
          {c.pipelineEyebrow}
        </div>
        <ol className="mt-5 space-y-2">
          {c.layers.map((l, i) => (
            <li
              key={l.label}
              className={`relative rounded-md ${layerColors[i]} px-4 py-3 transition`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/80">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-sm font-medium text-white">
                  {l.label}
                </span>
              </div>
              <div className="mt-1 text-right font-mono text-[10px] text-white/85">
                {l.note}
              </div>
              {i < c.layers.length - 1 && (
                <span className="absolute -bottom-2 left-1/2 h-2 w-px bg-white/20" />
              )}
            </li>
          ))}
        </ol>
        <div className="mt-6 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-white/75">
          <span>{c.footerLeft}</span>
          <span className="text-lime">{c.footerRight}</span>
        </div>
      </div>
    </div>
  );
}
